import React, { useState, useEffect } from 'react';
import Icon from '../AppIcon';
import Button from './Button';

const Notification = ({ notification, onDismiss }) => {
  const [isVisible, setIsVisible] = useState(false);
  const [isLeaving, setIsLeaving] = useState(false);

  useEffect(() => {
    // Trigger entrance animation
    const timer = setTimeout(() => setIsVisible(true), 50);
    return () => clearTimeout(timer);
  }, []);

  const handleDismiss = () => {
    setIsLeaving(true);
    setTimeout(() => onDismiss?.(), 300);
  };

  const getNotificationConfig = () => {
    switch (notification?.type) {
      case 'success':
        return {
          icon: 'CheckCircle',
          bgColor: 'bg-success/10 dark:bg-success/20',
          borderColor: 'border-success/20 dark:border-success/30',
          iconColor: 'text-success',
          titleColor: 'text-success'
        };
      case 'error':
        return {
          icon: 'XCircle',
          bgColor: 'bg-error/10 dark:bg-error/20',
          borderColor: 'border-error/20 dark:border-error/30',
          iconColor: 'text-error',
          titleColor: 'text-error'
        };
      case 'warning':
        return {
          icon: 'AlertTriangle',
          bgColor: 'bg-warning/10 dark:bg-warning/20',
          borderColor: 'border-warning/20 dark:border-warning/30',
          iconColor: 'text-warning',
          titleColor: 'text-warning'
        };
      case 'info':
      default:
        return {
          icon: 'Info',
          bgColor: 'bg-primary/10 dark:bg-primary/20',
          borderColor: 'border-primary/20 dark:border-primary/30',
          iconColor: 'text-primary',
          titleColor: 'text-primary'
        };
    }
  };

  const config = getNotificationConfig();

  return (
    <div
      className={`
        transform transition-all duration-300 ease-out
        ${isVisible && !isLeaving 
          ? 'translate-x-0 opacity-100 scale-100' :'translate-x-full opacity-0 scale-95'
        }
      `}
    >
      <div
        className={`
          p-4 rounded-lg border shadow-lg backdrop-blur-sm max-w-md w-full
          ${config?.bgColor} ${config?.borderColor}
        `}
      >
        <div className="flex items-start space-x-3">
          <div className={`flex-shrink-0 w-6 h-6 flex items-center justify-center`}>
            <Icon 
              name={config?.icon} 
              size={20} 
              className={config?.iconColor} 
            />
          </div>
          
          <div className="flex-1 min-w-0">
            {notification?.title && (
              <h4 className={`font-semibold text-sm mb-1 ${config?.titleColor}`}>
                {notification?.title}
              </h4>
            )}
            
            <p className="text-sm text-text-primary leading-relaxed">
              {notification?.message}
            </p>

            {notification?.actions && notification?.actions?.length > 0 && (
              <div className="flex items-center space-x-2 mt-3">
                {notification?.actions?.map((action, index) => (
                  <Button
                    key={index}
                    size="sm"
                    variant={action?.variant || "outline"}
                    onClick={() => {
                      action?.onClick?.();
                      if (!action?.keepOpen) {
                        handleDismiss();
                      }
                    }}
                    iconName={action?.icon}
                    iconPosition={action?.iconPosition || "left"}
                    className="text-xs"
                  >
                    {action?.label}
                  </Button>
                ))}
              </div>
            )}
          </div>

          {/* Close button */}
          <button
            onClick={handleDismiss}
            className="flex-shrink-0 p-1 rounded-full hover:bg-surface/50 transition-colors"
          >
            <Icon name="X" size={16} className="text-text-secondary" />
          </button>
        </div>

        {/* Progress bar for auto-dismiss */}
        {notification?.duration > 0 && !notification?.persistent && (
          <div className="absolute bottom-0 left-0 right-0 h-1 bg-surface/20 rounded-b-lg overflow-hidden">
            <div 
              className={`h-full bg-current opacity-30 ${config?.iconColor}`}
              style={{ 
                animation: `shrink-width ${notification?.duration}ms linear forwards`
              }}
            />
          </div>
        )}
      </div>
    </div>
  );
};

export default Notification;